import { useCallback } from 'react';
import { useSelector } from 'react-redux';
import { useAppDispatch } from '@store/hooks/useAppDispatch';
import { navigateBack, navigationRef } from '@helpers';
import { getSkipBottomSheetNavigation } from '@components/common/ScreenAnimations/helpers';
import {
	selectTopBottomSheet,
	selectPreCloseAction,
	setPreCloseAction,
	popBottomSheet,
} from '../../../../reducers/v2/bottomSheet/bottomSheet.slice';

export interface UseManageNavigatorBottomSheetWrapperParams {
	screenNameToRemove: string;
	bottomSheetId: string;
}

export const useManageNavigatorBottomSheetWrapper = ({ screenNameToRemove, bottomSheetId }: UseManageNavigatorBottomSheetWrapperParams) => {
	const dispatch = useAppDispatch();
	const topBottomSheet = useSelector(selectTopBottomSheet);
	const preCloseAction = useSelector(selectPreCloseAction);

	const onPresentedChange = useCallback(() => {
		// Check the global flag synchronously
		const shouldSkipNavigation = getSkipBottomSheetNavigation();

		// Execute the pre-close action if it exists
		if (preCloseAction) {
			preCloseAction();
			// Clear the action after executing
			dispatch(setPreCloseAction(null));
		}

		// Check if we should skip navigation (when navigating from header)
		// In this case, just remove the screen from nav - don't reset flag or clear stack
		// (webHeader handles that centrally after all animations complete)
		if (shouldSkipNavigation) {
			// Screen must be removed from the stack even when header already navigated,
			// otherwise the silk-hq portal overlay stays in the DOM blocking interaction.
			const rootState = navigationRef?.current?.getRootState();
			if (rootState) {
				const filteredRoutes = rootState.routes.filter((route: { name: string }) => route.name !== screenNameToRemove);
				if (filteredRoutes.length !== rootState.routes.length) {
					navigationRef?.current?.reset({
						...rootState,
						routes: filteredRoutes,
						index: filteredRoutes.length - 1,
					});
				}
			}
			// Don't reset flag or clear stack here - multiple sheets may need to clean up
			// webHeader handles this centrally after animations complete
			return;
		}

		// Pop from stack and navigate back
		const returnRoute = topBottomSheet?.returnRoute;
		dispatch(popBottomSheet());
		if (returnRoute) {
			navigationRef?.current?.navigate(returnRoute);
		} else {
			navigateBack();
		}
	}, [dispatch, topBottomSheet, preCloseAction, screenNameToRemove]);

	return { onPresentedChange };
};
